import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import Input from '../Input';
import type { TaxonomyKeyword } from '../../pages/settings/TaxonomySettingsPage';
import { apiClient } from '../../lib/api';

interface KeywordManagerProps {
  nodeId: string;
  nodeName: string;
  keywords: TaxonomyKeyword[];
  onKeywordsUpdated: () => void;
}

const KEYWORD_SUGGESTION_TOKEN_COST = 500;

export default function KeywordManager({ nodeId, nodeName, keywords, onKeywordsUpdated }: KeywordManagerProps) {
  const { t } = useTranslation();
  const [isAddingManual, setIsAddingManual] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newRelevance, setNewRelevance] = useState('0.8');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [error, setError] = useState('');
  const [suggestedKeywords, setSuggestedKeywords] = useState<Array<{ term: string; relevanceScore: number }>>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set());

  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) {
      setError('Keyword cannot be empty');
      return;
    }

    const relevance = parseFloat(newRelevance);
    if (isNaN(relevance) || relevance < 0 || relevance > 1) {
      setError('Relevance must be between 0 and 1');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await apiClient.post(`/taxonomy/${nodeId}/keywords`, {
        keywords: [
          {
            term: newKeyword.trim(),
            relevanceScore: relevance,
          },
        ],
      });

      // Reset form
      setNewKeyword('');
      setNewRelevance('0.8');
      setIsAddingManual(false);

      // Notify parent to refresh
      onKeywordsUpdated();
    } catch (err: any) {
      console.error('Failed to add keyword:', err);
      setError(err.response?.data?.message || 'Failed to add keyword');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteKeyword = async (keywordId: string) => {
    setIsSubmitting(true);
    setError('');

    try {
      await apiClient.delete(`/taxonomy/keywords/${keywordId}`);
      onKeywordsUpdated();
    } catch (err: any) {
      console.error('Failed to delete keyword:', err);
      setError(err.response?.data?.message || 'Failed to delete keyword');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuggestKeywords = async () => {
    setIsSuggesting(true);
    setError('');

    try {
      const response = await apiClient.post('/taxonomy/keywords/suggest', {
        nodeId: nodeId,
        count: 10,
      });

      const suggestions = response.data.data?.keywords || [];

      if (suggestions.length > 0) {
        // Map API response to our format
        const mappedSuggestions = suggestions.map((kw: any) => ({
          term: kw.term || kw.keyword,
          relevanceScore: kw.relevanceScore || 0.7,
        }));

        setSuggestedKeywords(mappedSuggestions);
        // Select all by default
        setSelectedSuggestions(new Set(mappedSuggestions.map((s: any) => s.term)));
      } else {
        setError('No keyword suggestions found');
        setShowSuggestModal(false);
      }
    } catch (err: any) {
      console.error('Failed to suggest keywords:', err);
      setError(err.response?.data?.message || 'Failed to suggest keywords');
      setShowSuggestModal(false);
    } finally {
      setIsSuggesting(false);
    }
  };

  const handleAddSelectedKeywords = async () => {
    if (selectedSuggestions.size === 0) {
      setError('Please select at least one keyword');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const keywordsToAdd = suggestedKeywords
        .filter(kw => selectedSuggestions.has(kw.term))
        .map(kw => ({
          term: kw.term,
          relevanceScore: kw.relevanceScore,
        }));

      await apiClient.post(`/taxonomy/${nodeId}/keywords`, {
        keywords: keywordsToAdd,
      });

      // Reset state and close modal
      setSuggestedKeywords([]);
      setSelectedSuggestions(new Set());
      setShowSuggestModal(false);
      onKeywordsUpdated();
    } catch (err: any) {
      console.error('Failed to add keywords:', err);
      setError(err.response?.data?.message || 'Failed to add keywords');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSuggestionSelection = (term: string) => {
    const newSelection = new Set(selectedSuggestions);
    if (newSelection.has(term)) {
      newSelection.delete(term);
    } else {
      newSelection.add(term);
    }
    setSelectedSuggestions(newSelection);
  };

  // Sort keywords by relevance (descending)
  const sortedKeywords = [...keywords].sort((a, b) => b.relevanceScore - a.relevanceScore);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h3 className="text-lg font-bold dark:text-white">{t('settings.taxonomy.keywords', 'Keywords')}</h3>
        <div className="flex gap-2 flex-wrap">
          {!isAddingManual && (
            <>
              <Button variant="secondary" onClick={() => setShowSuggestModal(true)} disabled={isSuggesting}>
                {t('settings.taxonomy.suggestKeywords', 'AI Suggestions')}
              </Button>
              <Button variant="secondary" onClick={() => setIsAddingManual(true)}>
                + {t('settings.taxonomy.addKeyword', 'Add')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 border-2 border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200 text-sm">
          {error}
        </div>
      )}

      {/* Manual Add Form */}
      {isAddingManual && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-gray-200 dark:border-gray-700 space-y-3">
          <Input
            label={t('settings.taxonomy.keywordText', 'Keyword')}
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            placeholder="e.g., machine learning, AI, technology"
            disabled={isSubmitting}
          />
          <Input
            label={t('settings.taxonomy.relevanceScore', 'Relevance Score (0-1)')}
            type="number"
            step="0.1"
            min="0"
            max="1"
            value={newRelevance}
            onChange={(e) => setNewRelevance(e.target.value)}
            disabled={isSubmitting}
          />
          <div className="flex gap-2">
            <Button
              onClick={handleAddKeyword}
              disabled={isSubmitting || !newKeyword.trim()}
            >
              {isSubmitting ? t('common.saving', 'Saving...') : t('common.add', 'Add')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setIsAddingManual(false);
                setNewKeyword('');
                setNewRelevance('0.8');
                setError('');
              }}
              disabled={isSubmitting}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* Keywords List */}
      {sortedKeywords.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {sortedKeywords.map((keyword) => (
            <div
              key={keyword.id}
              className="group flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-sm hover:border-gray-400 dark:hover:border-gray-500 transition"
            >
              <span className="dark:text-white font-medium">{keyword.term}</span>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {Math.round(keyword.relevanceScore * 100)}%
              </span>
              <button
                onClick={() => handleDeleteKeyword(keyword.id)}
                className="ml-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                disabled={isSubmitting}
                title={t('common.delete', 'Delete')}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('settings.taxonomy.noKeywords', 'No keywords yet. Add keywords manually or use AI suggestions.')}
        </p>
      )}

      {/* Suggest Keywords Modal */}
      {showSuggestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 border-2 border-black dark:border-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-3 dark:text-white">
              {t('settings.taxonomy.keywordSuggestions', 'AI Keyword Suggestions')}
            </h3>

            {/* Loading state */}
            {isSuggesting && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white mb-3"></div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('settings.taxonomy.generatingSuggestions', 'Generating keyword suggestions...')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                  Cost: {KEYWORD_SUGGESTION_TOKEN_COST} tokens • Duration: 10-20 seconds
                </p>
              </div>
            )}

            {/* No suggestions yet - show confirm button */}
            {!isSuggesting && suggestedKeywords.length === 0 && (
              <>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {t(
                    'settings.taxonomy.keywordSuggestionDescription',
                    'AI will analyze "{{nodeName}}" and suggest relevant keywords based on its context and description.',
                    { nodeName }
                  )}
                </p>
                <div className="flex gap-2 justify-end">
                  <Button variant="secondary" onClick={() => setShowSuggestModal(false)}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button onClick={handleSuggestKeywords}>
                    {t('settings.taxonomy.generateSuggestions', 'Generate Suggestions')}
                  </Button>
                </div>
              </>
            )}

            {/* Suggestions list with checkboxes */}
            {!isSuggesting && suggestedKeywords.length > 0 && (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('settings.taxonomy.selectKeywordsToAdd', 'Select keywords you want to add:')}
                </p>

                <div className="space-y-2 mb-4 max-h-96 overflow-y-auto">
                  {suggestedKeywords.map((kw) => (
                    <label
                      key={kw.term}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer transition"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSuggestions.has(kw.term)}
                        onChange={() => toggleSuggestionSelection(kw.term)}
                        className="w-4 h-4"
                      />
                      <div className="flex-1">
                        <span className="font-medium dark:text-white">{kw.term}</span>
                        <span className="ml-2 text-xs text-gray-600 dark:text-gray-400">
                          {Math.round(kw.relevanceScore * 100)}% relevance
                        </span>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="flex gap-2 justify-between items-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedSuggestions.size} of {suggestedKeywords.length} selected
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setSuggestedKeywords([]);
                        setSelectedSuggestions(new Set());
                        setShowSuggestModal(false);
                      }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                      onClick={handleAddSelectedKeywords}
                      disabled={isSubmitting || selectedSuggestions.size === 0}
                    >
                      {isSubmitting
                        ? t('common.adding', 'Adding...')
                        : t('settings.taxonomy.addSelected', 'Add Selected')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
